/**
 * PremiumEffects - Componentes de efectos visuales premium
 * Partículas flotantes, indicadores en vivo, transiciones
 */
import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import '../styles/premium-effects.css';

// ============================================
// PARTÍCULAS FLOTANTES DE FONDO
// ============================================
export const FloatingParticles = memo(({ count = 30 }) => {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 20}s`,
    size: Math.random() * 2 + 1,
  }));

  return (
    <div className="particles-container" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            animationDelay: p.animationDelay,
            width: `${p.size}px`,
            height: `${p.size}px`,
          }}
        />
      ))}
    </div>
  );
});

FloatingParticles.displayName = 'FloatingParticles';

// ============================================
// INDICADOR DE CONEXIÓN EN VIVO
// ============================================
export const LiveConnectionIndicator = memo(({ isConnected = true }) => {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className={`live-indicator ${isConnected ? 'online' : 'offline'}`}
      title={`Última actualización: ${lastUpdate.toLocaleTimeString('es-ES')}`}
    >
      <span>{isConnected ? 'EN VIVO' : 'DESCONECTADO'}</span>
    </div>
  );
});

LiveConnectionIndicator.displayName = 'LiveConnectionIndicator';

// ============================================
// CONTADOR ANIMADO
// ============================================
export const AnimatedCounter = memo(({ value, duration = 500, prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current === value) return;
    
    setIsUpdating(true);
    const startValue = prevValue.current;
    const endValue = value;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(startValue + (endValue - startValue) * easeOutQuart);
      
      setDisplayValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsUpdating(false);
        prevValue.current = value;
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span className={`counter-animate ${isUpdating ? 'updating' : ''}`}>
      {prefix}{displayValue.toLocaleString('es-ES')}{suffix}
    </span>
  );
});

AnimatedCounter.displayName = 'AnimatedCounter';

// ============================================
// BADGE CON PULSO
// ============================================
export const PulseBadge = memo(({ count, color = 'green', showZero = false }) => {
  const [isNew, setIsNew] = useState(false);
  const prevCount = useRef(count);

  useEffect(() => {
    if (count > prevCount.current) {
      setIsNew(true);
      const timer = setTimeout(() => setIsNew(false), 300);
      return () => clearTimeout(timer);
    }
    prevCount.current = count;
  }, [count]);

  if (!showZero && count === 0) return null;

  const colors = {
    green: {
      bg: 'linear-gradient(135deg, #00ff6a 0%, #00cc55 100%)',
      shadow: '0 0 10px rgba(0, 255, 106, 0.6)',
      text: '#000',
    },
    red: {
      bg: 'linear-gradient(135deg, #ff6b6b 0%, #ff4444 100%)',
      shadow: '0 0 10px rgba(255, 68, 68, 0.6)',
      text: '#fff',
    },
    orange: {
      bg: 'linear-gradient(135deg, #ff9500 0%, #ff6b00 100%)',
      shadow: '0 0 10px rgba(255, 149, 0, 0.6)',
      text: '#000',
    },
    cyan: {
      bg: 'linear-gradient(135deg, #00dcff 0%, #00a8cc 100%)',
      shadow: '0 0 10px rgba(0, 220, 255, 0.6)',
      text: '#000',
    },
  };

  const style = colors[color] || colors.green;

  return (
    <span
      className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
        isNew ? 'badge-animated' : ''
      } ${count > 0 ? 'badge-pulse' : ''}`}
      style={{
        background: style.bg,
        color: style.text,
        boxShadow: style.shadow,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
});

PulseBadge.displayName = 'PulseBadge';

// ============================================
// EFECTO DE RIPPLE EN CLICK
// ============================================
export const useRipple = () => {
  const createRipple = useCallback((event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = document.createElement('span');
    ripple.className = 'ripple';
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  }, []);

  return createRipple;
};

// ============================================
// TRANSICIÓN DE PÁGINA
// ============================================
export const PageTransition = memo(({ children, isActive }) => {
  const [shouldRender, setShouldRender] = useState(isActive);
  const [animationClass, setAnimationClass] = useState('');

  useEffect(() => {
    if (isActive) {
      setShouldRender(true);
      setTimeout(() => setAnimationClass('page-transition-enter-active'), 10);
    } else {
      setAnimationClass('page-transition-exit-active');
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isActive]);

  if (!shouldRender) return null;

  return (
    <div className={`page-transition-enter ${animationClass}`}>
      {children}
    </div>
  );
});

PageTransition.displayName = 'PageTransition';

// ============================================
// CONFETTI CELEBRATION
// ============================================
export const Confetti = memo(({ active, duration = 3000 }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!active) return;

    const colors = ['#00ff6a', '#00dcff', '#ff9500', '#ff6b6b', '#a855f7', '#ffd700'];
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: `${Math.random() * 0.5}s`,
      rotation: Math.random() * 360,
    }));

    setParticles(newParticles);

    const timer = setTimeout(() => setParticles([]), duration);
    return () => clearTimeout(timer);
  }, [active, duration]);

  if (particles.length === 0) return null;

  return (
    <div className="confetti-container">
      {particles.map((p) => (
        <div
          key={p.id}
          className="confetti"
          style={{
            left: p.left,
            backgroundColor: p.color,
            animationDelay: p.delay,
            transform: `rotate(${p.rotation}deg)`,
          }}
        />
      ))}
    </div>
  );
});

Confetti.displayName = 'Confetti';

// ============================================
// PRICE FLASH EFFECT
// ============================================
export const usePriceFlash = () => {
  const flashPrice = useCallback((element, direction) => {
    if (!element) return;
    
    element.classList.remove('data-flash-green', 'data-flash-red');
    // Force reflow
    void element.offsetWidth;
    element.classList.add(direction === 'up' ? 'data-flash-green' : 'data-flash-red');
  }, []);

  return flashPrice;
};

// ============================================
// TYPING EFFECT
// ============================================
export const TypingText = memo(({ text, speed = 50, onComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayText('');
    setIsComplete(false);
    
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setIsComplete(true);
        onComplete && onComplete();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onComplete]);

  return (
    <span>
      {displayText}
      {!isComplete && <span className="animate-pulse">|</span>}
    </span>
  );
});

TypingText.displayName = 'TypingText';

// ============================================
// GLOW CARD WRAPPER
// ============================================
export const GlowCard = memo(({ children, className = '', glowColor = '#00ffff' }) => {
  return (
    <div 
      className={`glass-card rounded-xl ${className}`}
      style={{
        '--glow-color': glowColor,
      }}
    >
      {children}
    </div>
  );
});

GlowCard.displayName = 'GlowCard';

export default {
  FloatingParticles,
  LiveConnectionIndicator,
  AnimatedCounter,
  PulseBadge,
  useRipple,
  PageTransition,
  Confetti,
  usePriceFlash,
  TypingText,
  GlowCard,
};
